// frontend/src/pages/public/PricingPage.jsx
import React, { useEffect, useState } from "react";
import { CheckIcon, MinusIcon } from "@heroicons/react/20/solid";
import Seo from "../../marketing/Seo";
import { Container, Section, Eyebrow, Badge, Button, cx } from "../../marketing/ui";
import { FAQAccordion } from "../../marketing/blocks";
import {
  PLANS,
  COMPARISON,
  PRICING_FAQ,
  ANNUAL_DISCOUNT,
  formatPrice,
} from "../../marketing/data/pricing";

/** Render a comparison-cell value: ✓ / — / text. */
function Cell({ value }) {
  if (value === true) return <CheckIcon className="mx-auto h-5 w-5 text-accent-600" aria-label="Included" />;
  if (value === false) return <MinusIcon className="mx-auto h-5 w-5 text-gray-300" aria-label="Not included" />;
  return <span className="text-sm text-gray-700">{value}</span>;
}

/** Format integer cents with a currency symbol; whole units drop decimals. */
function money(cents, symbol) {
  const v = cents / 100;
  return v % 1 === 0 ? `${symbol}${v}` : `${symbol}${v.toFixed(2)}`;
}

/** When the promo ends, e.g. "ends Aug 1". */
function fmtEnds(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  // Live pricing response { currency, currency_symbol, plans{} }; null until
  // loaded or on failure, in which case the static PLANS values are shown.
  // QRFlow sells in EUR only — no currency selection.
  const [live, setLive] = useState(null);

  useEffect(() => {
    if (typeof fetch !== "function") return undefined;
    let active = true;
    fetch("/api/public/pricing")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!active || !data) return;
        const map = {};
        for (const p of data.plans) map[p.plan_name.toLowerCase()] = p;
        setLive({
          currency: data.currency,
          symbol: data.currency_symbol,
          plans: map,
        });
      })
      .catch(() => {
        /* keep static fallback pricing on any failure */
      });
    return () => {
      active = false;
    };
  }, []);

  const symbol = live?.symbol || "€";

  /** Resolve the display price for a static plan, preferring live data. */
  const priceFor = (plan) => {
    const entry = live?.plans?.[plan.id];
    const point = entry && (annual ? entry.annual : entry.monthly);
    if (!point || point.cents == null) {
      // Static fallback (USD; annual derived from the static discount).
      return { display: formatPrice(plan.monthly, annual) };
    }
    // Annual prices are yearly totals — show the per-month equivalent.
    const perMonth = (c) => (annual ? c / 12 : c);
    return {
      display: money(perMonth(point.cents), symbol),
      original:
        point.is_promo && point.original_cents != null
          ? money(perMonth(point.original_cents), symbol)
          : null,
      promoEnds: point.is_promo ? fmtEnds(point.promo_ends_at) : null,
      upcoming:
        !annual && point.upcoming_cents != null
          ? `${money(point.upcoming_cents, symbol)}/mo from ${fmtEnds(point.upcoming_date)}`
          : null,
    };
  };

  // Comparison table's price row, derived from the same `priceFor` used by
  // the plan cards above so the two sections can never disagree — this row
  // used to be a second, independently hardcoded price snapshot.
  const comparisonRows = [
    {
      label: COMPARISON[0].label,
      ...Object.fromEntries(PLANS.map((p) => [p.id, priceFor(p).display])),
    },
    ...COMPARISON.slice(1),
  ];

  return (
    <>
      <Seo
        title="Pricing"
        description="Simple, honest QRFlow pricing. Free, Starter €19, Pro €59, Business €149. Unlimited dynamic redirects, no per-scan fees."
        path="/pricing"
      />

      {/* Header + toggle */}
      <Section className="pb-8">
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow>Pricing</Eyebrow>
          <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Honest pricing that scales with you
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            Every plan includes unlimited dynamic redirects. No per-scan fees, no monthly
            code caps — just clear limits and the features you actually need.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <span className={cx("text-sm font-medium", !annual ? "text-gray-900" : "text-gray-500")}>
            Monthly
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={annual}
            onClick={() => setAnnual((v) => !v)}
            className={cx(
              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
              annual ? "bg-brand-600" : "bg-gray-300",
            )}
          >
            <span
              className={cx(
                "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
                annual ? "translate-x-5" : "translate-x-0.5",
              )}
            />
          </button>
          <span className={cx("text-sm font-medium", annual ? "text-gray-900" : "text-gray-500")}>
            Annual
          </span>
          <Badge tone="accent">Save {Math.round(ANNUAL_DISCOUNT * 100)}%</Badge>
        </div>
      </Section>

      {/* Plan cards */}
      <Container className="pb-8">
        <div className="grid gap-6 lg:grid-cols-4">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={cx(
                "flex flex-col rounded-2xl bg-white p-6 ring-1",
                plan.highlight ? "ring-2 ring-brand-600 shadow-lg" : "ring-gray-200",
              )}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">{plan.name}</h2>
                {plan.badge && <Badge>{plan.badge}</Badge>}
              </div>
              <p className="mt-1 text-sm text-gray-500">{plan.tagline}</p>
              {(() => {
                const price = priceFor(plan);
                return (
                  <>
                    <div className="mt-5 flex items-baseline gap-2">
                      {price.original && (
                        <span className="text-xl font-semibold text-gray-400 line-through">
                          {price.original}
                        </span>
                      )}
                      <span className="text-4xl font-bold tracking-tight text-gray-900">
                        {price.display}
                      </span>
                      <span className="text-sm text-gray-500">/mo</span>
                    </div>
                    {price.promoEnds && (
                      <p className="mt-1 text-xs font-semibold text-accent-700">
                        Limited offer — ends {price.promoEnds}
                      </p>
                    )}
                    {!price.promoEnds && price.upcoming && (
                      <p className="mt-1 text-xs text-gray-500">
                        {price.upcoming}
                      </p>
                    )}
                    {annual && plan.monthly > 0 && (
                      <p className="mt-1 text-xs text-accent-700">
                        billed annually
                      </p>
                    )}
                  </>
                );
              })()}
              <Button
                to="/register"
                variant={plan.highlight ? "primary" : "secondary"}
                className="mt-6 w-full"
              >
                {plan.cta}
              </Button>
              <ul className="mt-6 space-y-3 border-t border-gray-100 pt-6 text-sm text-gray-700">
                {Object.values(plan.limits).map((l) => (
                  <li key={l} className="font-medium text-gray-900">{l}</li>
                ))}
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckIcon className="mt-0.5 h-5 w-5 flex-none text-accent-600" aria-hidden="true" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Container>

      {/* Honest pricing callout */}
      <Section className="py-10">
        <div className="rounded-2xl bg-brand-50 p-8 ring-1 ring-brand-100">
          <h2 className="text-xl font-semibold text-gray-900">Why our pricing is honest</h2>
          <div className="mt-4 grid gap-6 sm:grid-cols-3">
            <div>
              <p className="font-medium text-gray-900">No per-scan fees</p>
              <p className="mt-1 text-sm text-gray-600">
                Your redirects keep working past your allowance — we never bill surprise overage.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-900">No monthly code caps</p>
              <p className="mt-1 text-sm text-gray-600">
                Limits are on active codes, not how many you may create each month.
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Generous tiers</p>
              <p className="mt-1 text-sm text-gray-600">
                500 codes on Pro, 5,000 on Business — far more headroom per dollar.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Comparison table */}
      <Section muted>
        <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900">
          Compare all features
        </h2>
        <div className="mt-10 overflow-x-auto">
          <table data-testid="comparison-table" className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-3 pr-4 text-sm font-semibold text-gray-900">Feature</th>
                {PLANS.map((p) => (
                  <th key={p.id} className="px-3 py-3 text-center text-sm font-semibold text-gray-900">
                    {p.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.label} className="border-b border-gray-100">
                  <td className="py-3 pr-4 text-sm text-gray-700">{row.label}</td>
                  <td className="px-3 py-3 text-center"><Cell value={row.free} /></td>
                  <td className="px-3 py-3 text-center"><Cell value={row.starter} /></td>
                  <td className="px-3 py-3 text-center"><Cell value={row.pro} /></td>
                  <td className="px-3 py-3 text-center"><Cell value={row.business} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <h2 className="text-center text-2xl font-bold tracking-tight text-gray-900">
          Pricing questions
        </h2>
        <div className="mt-10">
          <FAQAccordion items={PRICING_FAQ} />
        </div>
        <div className="mt-12 text-center">
          <Button to="/register" size="lg">Start free — no card required</Button>
        </div>
      </Section>
    </>
  );
}
